import { useState } from 'react';
import { connect } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { isMobile } from 'shared/util/mobile';
import type { ApiQueryParams, Month, NumberRange, PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { actions } from '@/state/filterActions';
import DurationSlider from '../slider/DurationSlider';
import { MonthCalendar } from './MonthCalendar';

interface TravelMonthsProps {
    months: Month[];
    duration?: NumberRange;
    toggleMonth: (month: Month) => void;
    resetMonths: () => void;
    triggerFetch: (apiUrl: string, queryParam?: ApiQueryParams) => void;
    className?: string;
}

const monthNames = displayValues.months as Record<string, string>[];

const isElemWithinDepartureContainer = (elem: HTMLElement | null): boolean =>
    (elem !== document.body && elem && (elem.id === 'departure-container' || isElemWithinDepartureContainer(elem.parentElement))) || false;

export const generateDateText = (months: Month[], duration?: NumberRange): string => {
    const daysText = duration ? `${duration[0]} - ${duration[1]} dager` : 'Når som helst';
    if (months.length === 0 && !duration) {
        return 'Når som helst';
    }
    if (months.length === 0) {
        return `${daysText} når som helst`;
    }
    if (months.length === 1) {
        return `${daysText} i ${monthNames[months[0].month - 1].full}`;
    }
    return `${daysText} i ${months.map((month) => monthNames[month.month - 1].short).join(', ')}`;
};

export const TravelMonths = ({ months, duration, toggleMonth, resetMonths, triggerFetch, className }: TravelMonthsProps) => {
    const { apiUrl } = useConfig();
    const [visibleDepartureInfo, setVisibleDepartureInfo] = useState(false);
    const [firstMonth] = useState(new Date().getMonth());
    const [firstYear] = useState(new Date().getFullYear());

    const hideDepartureContainerIfClickedOutside = (event: Event): void => {
        if (!isElemWithinDepartureContainer(event.target as HTMLElement)) {
            hideDepartureInfo();
        }
    };

    const hideDepartureInfo = (): void => {
        if (!isMobile()) {
            window.removeEventListener('click', hideDepartureContainerIfClickedOutside, true);
            window.removeEventListener('keyup', hideDepartureContainerIfClickedOutside, true);
            window.removeEventListener('touchstart', hideDepartureContainerIfClickedOutside, true);
        }
        setVisibleDepartureInfo(false);
    };

    const departureButtonOnClick = (): void => {
        if (!isMobile()) {
            if (visibleDepartureInfo) {
                window.removeEventListener('click', hideDepartureContainerIfClickedOutside, true);
                window.removeEventListener('keyup', hideDepartureContainerIfClickedOutside, true);
                window.removeEventListener('touchstart', hideDepartureContainerIfClickedOutside, true);
            } else {
                window.addEventListener('click', hideDepartureContainerIfClickedOutside, true);
                window.addEventListener('keyup', hideDepartureContainerIfClickedOutside, true);
                window.addEventListener('touchstart', hideDepartureContainerIfClickedOutside, true);
            }
        }
        setVisibleDepartureInfo(!visibleDepartureInfo);
    };

    const toggleMonthAndTriggerFetch = (month: Month): void => {
        toggleMonth(month);
        triggerFetch(apiUrl);
    };

    const resetMonthsAndTriggerFetch = (): void => {
        resetMonths();
        triggerFetch(apiUrl);
    };

    const renderDepartureInfo = () => (
        <div className="u-pa16 u-licorice u-border-radius-8px box-shadow--gt768">
            <MonthCalendar
                title="Velg måneder"
                firstMonth={firstMonth}
                firstYear={firstYear}
                size={12}
                initialValues={months}
                toggleMonth={toggleMonthAndTriggerFetch}
                resetMonths={resetMonthsAndTriggerFetch}
                close={hideDepartureInfo}
                data-testid="departure-months-calendar"
            />
            <DurationSlider triggerFetch={triggerFetch} />
        </div>
    );

    const renderDepartureInfoDesktop = () => <div className="dropdown-list">{renderDepartureInfo()}</div>;

    const renderDepartureInfoMobile = () => (
        <div className="fullscreen-list u-bg-ice u-pv16">
            <div className="scrollable-list-container">{renderDepartureInfo()}</div>
        </div>
    );

    return (
        <div className={`dropdown-container ${className || ''}`}>
            <label htmlFor="departureDropdown" className="u-screen-reader-only">
                Velg måneder og reisende
            </label>
            <button
                type="button"
                id="departureDropdown"
                onClick={departureButtonOnClick}
                className="transparent u-r-size1of1 input input--select__wrap u-mb16"
                data-testid="departureDropdown">
                <div className="dropdown-display clicked">
                    <span data-testid="departure-text" className="u-pl8 u-text-left no-text-select u-licorice">
                        {generateDateText(months, duration)}
                    </span>
                </div>
            </button>
            {visibleDepartureInfo && (isMobile() ? renderDepartureInfoMobile() : renderDepartureInfoDesktop())}
        </div>
    );
};

const mapStateToProps = (
    state: PaleState,
    { triggerFetch }: Pick<TravelMonthsProps, 'triggerFetch'>,
): Pick<TravelMonthsProps, 'months' | 'duration' | 'triggerFetch'> => ({
    months: state.filters.travelMonths,
    duration: state.filters.duration,
    triggerFetch: triggerFetch,
});

const mapDispatchToProps = {
    toggleMonth: actions.toggleTravelMonth,
    resetMonths: actions.resetTravelMonths,
};

export default connect(mapStateToProps, mapDispatchToProps)(TravelMonths);
